<!-- 
 >> Schema 
options:[{
   "text":<string>
   "value":<string>
}] 
dynamicOptions:<string>
-->
<template>
   <div
      class="select-field"
      ref="el"
      :class="[
         inputStyle,
         { 'has-error': !!errorMessage, success: meta.valid },
      ]"
      v-click-away="closeDD"
   >
      <div
         class="fake-select"
         ref="fieldEl"
         :class="{ active: ddActive }"
         @click="toggleDD"
      >
         <template v-if="fieldValue && readableValue">
            <span class="selected">{{ readableValue }}</span>
         </template>
         <p v-if="!fieldValue">{{ placeholder }}</p>
      </div>
      <transition @enter="handleEnter" @leave="handleLeave">
         <div class="dropdown" v-if="ddActive" data-lenis-prevent>
            <div
               v-for="({ tag, text, value, ...childAttrs }, idx) in ddOptions"
               class="option"
               :class="{ selected: fieldValue === value }"
               @click="selectValue(value)"
               :key="idx"
            >
               {{ text }}<IconCheck v-if="fieldValue === value" />
            </div>
         </div>
      </transition>
   </div>
</template>

<script setup lang="ts">
import { clickAway as vClickAway } from "~/directives/clickAway.js";
import IconCheck from "~/assets/images/icons/check.svg";
import { useField } from "vee-validate";
const gsap = useNuxtApp().$gsap;
const emit = defineEmits(["change"]);
const ddActive = ref(false);
const ddOptions = ref([]);
const props = defineProps({
   value: {
      type: [String, Number],
      default: undefined,
   },
   name: {
      type: String,
      required: true,
   },
   placeholder: {
      type: String,
      default: "",
   },
   inputStyle: {
      type: String,
      default: "normal",
      validator: (value) => ["simple", "normal"].includes(value),
   },
   options: {
      type: Array,
      required: false,
      default: null,
   },
   dynamicOptions: {
      type: String,
      required: false,
      default: null,
   },
});
const name = toRef(props, "name");
// Rules are set at form level
const {
   value: fieldValue,
   errorMessage,
   setTouched,
   setValue,
   meta,
} = useField(name, undefined, {
   initialValue: props.value ?? null,
});

// >> Methods
const toggleDD = () => {
   ddActive.value = !ddActive.value;
};
const closeDD = () => {
   ddActive.value = false;
};
const selectValue = (value) => {
   fieldValue.value = value;
   emit("change", value);
   setValue(value, true);
   ddActive.value = false;
};
// Watch route to update value
// used when the page doens't change, but the route is updated (ex: query param change)
const route = useRoute();
watch(
   () => route.query,
   (newValue, oldVal) => {
      if (newValue[name.value] !== oldVal[name.value]) {
         selectValue(newValue[name.value] ?? ddOptions.value[0].value);
      }
   },
);

// >> COMPUTED
const readableValue = computed(() => {
   const match = ddOptions.value?.filter((op) => op.value == fieldValue.value);
   return match[0]?.text ?? "";
});

//>> Animations
const handleEnter = (el, done) => {
   const dropdownHeight = gsap.utils.clamp(0, 350, el.scrollHeight);
   const elRect = el.getBoundingClientRect();
   // console.log(elRect);
   if (elRect.bottom + dropdownHeight > window.innerHeight) {
      gsap.set(el, {
         top: "auto",
         bottom: "100%",
      });
   } else {
      gsap.set(el, {
         top: "100%",
         bottom: "auto",
      });
   }
   gsap.to(el, {
      height: dropdownHeight,
      onComplete: done,
      duration: 0.5,
      ease: "cubic.inOut",
   });
};
const handleLeave = (el, done) => {
   gsap.to(el, {
      height: "0px",
      onComplete: done,
      duration: 0.5,
      ease: "cubic.inOut",
   });
};

onMounted(async () => {
   if (props?.dynamicOptions) {
      ddOptions.value = await useMultiSelectData(props.dynamicOptions);
   } else {
      ddOptions.value = props.options;
   }
   await nextTick();
   selectValue(fieldValue.value ?? ddOptions.value[0].value);
});
</script>
<style scoped lang="scss">
@import "~/assets/scss/partials/select-fields";
</style>
